
import React from 'react';
import { Shield, Lock, Users, Video, MessageSquare, Zap, Sparkles, Bell, Globe, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';

const About: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const features = [
    {
      icon: <Video />,
      title: 'Video Chat',
      description: 'Connect face-to-face with people from around the world in high-quality video calls.'
    },
    {
      icon: <MessageSquare />,
      title: 'Text Messaging',
      description: 'Send instant messages during video calls or when video is turned off.'
    },
    {
      icon: <Zap />,
      title: 'Instant Connections',
      description: 'Get matched with new conversation partners with a single click.'
    },
    {
      icon: <Shield />,
      title: 'Privacy Protection',
      description: 'Your personal information is protected with end-to-end encryption.'
    },
    {
      icon: <Bell />,
      title: 'Notifications',
      description: 'Get notified of new messages and connections even when the app is in background.'
    },
    {
      icon: <Globe />,
      title: 'Global Community',
      description: 'Connect with people from different cultures and backgrounds worldwide.'
    }
  ];
  
  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="py-24 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Chatrix</h1>
          
          <p className="text-xl text-foreground/80 mb-8 max-w-3xl mx-auto">
            Chatrix is a secure, privacy-focused video and text chat platform designed to connect people from around the world in meaningful conversations.
          </p>
          
          <Link to="/chat">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              Start Chatting Now
            </Button>
          </Link>
        </motion.div>
        
        {/* Features Section */}
        <section className="py-16">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl md:text-4xl font-bold mb-4"
            >
              Key Features
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl text-foreground/80 max-w-2xl mx-auto"
            >
              Everything you need for meaningful online connections.
            </motion.p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={cn(
                  "p-6 rounded-xl border",
                  isDark
                    ? "bg-[#151830]/50 border-blue-900/20 hover:bg-[#151830]"
                    : "bg-white/50 border-blue-100/50 hover:bg-white"
                )}
              >
                <div className={cn(
                  "w-12 h-12 rounded-lg flex items-center justify-center mb-4",
                  isDark
                    ? "bg-blue-900/20 text-blue-400"
                    : "bg-blue-50 text-blue-500"
                )}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className={cn(
                  "text-base",
                  isDark ? "text-gray-300" : "text-gray-600"
                )}>
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>
        
        {/* Mission Section */}
        <section className="py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-lg mb-6">
                Our mission is to create a safe, enjoyable space for genuine human connection online. 
                We believe that technology should enhance communication, not compromise privacy or security.
              </p>
              
              <ul className="space-y-3">
                {[
                  "Create meaningful connections across borders",
                  "Protect user privacy and data security",
                  "Build a respectful and inclusive community", 
                  "Make quality video chat accessible to all"
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <Check className={cn(
                      "mt-1 mr-2 h-5 w-5 flex-shrink-0",
                      isDark ? "text-blue-400" : "text-blue-500"
                    )} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={cn(
                "rounded-xl p-1 border shadow-lg overflow-hidden",
                isDark 
                  ? "bg-[#151830]/30 border-blue-900/20" 
                  : "bg-white/80 border-blue-100/50"
              )}
            >
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 aspect-video rounded-lg flex items-center justify-center p-8 text-white">
                <div className="text-center">
                  <Sparkles size={64} className="mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2">Join Our Community</h3>
                  <p className="mb-4">Connect with people who share your interests</p>
                  <Link to="/signup">
                    <Button className="bg-white text-blue-600 hover:bg-blue-50">
                      Sign Up Now
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
        
        {/* Core Values Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl font-bold mb-4"
            >
              Core Values
            </motion.h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={cn(
                "rounded-xl p-6 text-center",
                isDark 
                  ? "bg-[#151830]/50 border border-blue-900/20" 
                  : "bg-blue-50/50 border border-blue-100/50"
              )}
            >
              <Shield className={cn("h-12 w-12 mx-auto mb-4", isDark ? "text-blue-400" : "text-blue-500")} />
              <h3 className="text-xl font-semibold mb-3">Privacy First</h3>
              <p className={cn("text-base", isDark ? "text-gray-300" : "text-gray-600")}>
                We prioritize your privacy with robust end-to-end encryption and minimal data collection.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className={cn(
                "rounded-xl p-6 text-center",
                isDark 
                  ? "bg-[#151830]/50 border border-blue-900/20" 
                  : "bg-blue-50/50 border border-blue-100/50"
              )}
            >
              <Lock className={cn("h-12 w-12 mx-auto mb-4", isDark ? "text-blue-400" : "text-blue-500")} />
              <h3 className="text-xl font-semibold mb-3">Security</h3>
              <p className={cn("text-base", isDark ? "text-gray-300" : "text-gray-600")}>
                Your safety is our priority with advanced security measures and active moderation.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className={cn(
                "rounded-xl p-6 text-center",
                isDark 
                  ? "bg-[#151830]/50 border border-blue-900/20" 
                  : "bg-blue-50/50 border border-blue-100/50"
              )}
            >
              <Users className={cn("h-12 w-12 mx-auto mb-4", isDark ? "text-blue-400" : "text-blue-500")} />
              <h3 className="text-xl font-semibold mb-3">Community</h3>
              <p className={cn("text-base", isDark ? "text-gray-300" : "text-gray-600")}>
                We foster a respectful community where meaningful connections can flourish.
              </p>
            </motion.div>
          </div>
        </section>
        
        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={cn(
            "rounded-2xl p-12 my-16 text-center",
            isDark 
              ? "bg-gradient-to-br from-[#151830] to-[#1c1e3a] border border-blue-900/20" 
              : "bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100/50"
          )}
        >
          <h2 className="text-3xl font-bold mb-6">Ready to Experience Chatrix?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join thousands of users already connecting through our platform.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/signup">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                Create Account
              </Button>
            </Link>
            <Link to="/chat">
              <Button size="lg" variant="outline" className={cn(
                "border-2",
                isDark 
                  ? "border-blue-500/30 text-blue-400 hover:bg-blue-900/20" 
                  : "border-blue-600/30 text-blue-600 hover:bg-blue-50"
              )}>
                Try Without Account
              </Button>
            </Link>
          </div>
        </motion.section>
        
        {/* Contact Section */}
        <section className="py-10">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="mb-6">
              Have questions or feedback? We'd love to hear from you at <a href="mailto:hello@chatrix.fun" className="text-blue-500 hover:underline">hello@chatrix.fun</a>.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
